import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Library, Sparkles, Share2, Copy, Trash2, Search, Clock, Download, User as UserIcon, Users2 } from "lucide-react";
import Markdown from "../../components/Markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const TYPE_ICON = { private: UserIcon, clinic: Users2, mixer: Users2, open_play: Users2, available_slot: Clock };

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function PlansLibrary() {
  const [plans, setPlans] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState("");
  const [openPlan, setOpenPlan] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [importing, setImporting] = useState(false);

  const importLegacy = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/plans/import-from-lessons");
      const { imported = 0, skipped = 0, total_eligible = 0 } = data || {};
      if (imported > 0) {
        toast.success(`Imported ${imported} plan${imported === 1 ? "" : "s"} from your existing lessons`);
        await load();
      } else if (skipped > 0) {
        toast.success(`Already up to date — ${skipped} plan${skipped === 1 ? " was" : "s were"} previously imported`);
      } else if (total_eligible === 0) {
        toast.message("No lessons with attached plans found to import");
      } else {
        toast.success("Nothing new to import");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };

  const load = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/plans");
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      // Distinguish "endpoint not deployed yet" (404) from auth/network errors so
      // the user sees something actionable instead of a blank empty state.
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      if (status === 404) {
        setLoadError("This feature isn't on the deployed app yet — please redeploy from the Emergent dashboard so the /api/plans endpoint is live.");
      } else if (status === 403) {
        setLoadError("Your account doesn't have access to the plan library.");
      } else if (status === 401) {
        setLoadError("Your session expired. Please sign in again.");
      } else {
        setLoadError(`Could not load plans (${status || "network"}): ${detail}`);
      }
      setPlans([]);
    }
  };

  useEffect(() => { load(); }, []);

  const open = async (p) => {
    try {
      const { data } = await api.get(`/plans/${p.id}`);
      setOpenPlan(data);
    } catch { toast.error("Could not load plan"); }
  };

  const remove = async (p, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this plan? This can't be undone.")) return;
    try {
      await api.delete(`/plans/${p.id}`);
      toast.success("Plan deleted");
      setPlans((cur) => cur.filter((x) => x.id !== p.id));
    } catch { toast.error("Could not delete"); }
  };

  const share = async (p, e) => {
    e?.stopPropagation();
    setSharingId(p.id);
    try {
      const { data } = await api.post(`/plans/${p.id}/share`);
      const url = `${window.location.origin}${data.share_path}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); }
      catch { toast.success(`Share link: ${url}`); }
      setPlans((cur) => cur.map((x) => x.id === p.id ? { ...x, share_token: data.share_token, is_public: true } : x));
    } catch { toast.error("Could not share"); }
    setSharingId(null);
  };

  const filtered = (plans || []).filter((p) => {
    if (!q.trim()) return true;
    const hay = `${p.title || ""} ${p.focus || ""} ${p.student_name || ""} ${(p.student_names || []).join(" ")} ${p.lesson_type || ""}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="space-y-8" data-testid="plans-library-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">AI Playbook</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Plan library</h1>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
            Every plan you've generated, ready to revisit, share, or duplicate. New plans are saved here automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <button
            onClick={importLegacy}
            disabled={importing}
            className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="plans-library-import"
            title="Pull plans from older lessons into your library — safe to click anytime"
          >
            <Download size={14} /> {importing ? "Importing…" : "Import legacy plans"}
          </button>
          <Link
            to="/coach/plans"
            className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-2"
            data-testid="plans-library-new"
          >
            <Sparkles size={14} /> Draft new plan
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2 max-w-md border-b border-white/15 focus-within:border-[#38BDF8]">
        <Search size={14} className="text-[#A3A3A3]" />
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, focus, or student…"
          className="bg-transparent outline-none py-2 text-sm flex-1"
          data-testid="plans-library-search"
        />
      </div>

      {plans === null ? (
        <div className="text-sm text-[#A3A3A3]" data-testid="plans-library-loading">Loading…</div>
      ) : loadError ? (
        <div className="card-surface p-10 text-center max-w-xl mx-auto" data-testid="plans-library-error">
          <div className="w-14 h-14 mx-auto border border-amber-400/50 grid place-items-center mb-4">
            <Library size={20} className="text-amber-400" />
          </div>
          <div className="font-display text-2xl font-black uppercase tracking-tight">Couldn't load plans</div>
          <p className="text-sm text-[#A3A3A3] mt-3 whitespace-pre-line">{loadError}</p>
          <button
            onClick={load}
            className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2 mt-6"
            data-testid="plans-library-retry"
          >Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="plans-library-empty">
          <div className="w-14 h-14 mx-auto border border-[#38BDF8]/50 grid place-items-center mb-4">
            <Library size={20} className="text-[#38BDF8]" />
          </div>
          <div className="font-display text-2xl font-black uppercase tracking-tight">
            {q ? "No matches" : "No plans yet"}
          </div>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-md mx-auto">
            {q
              ? "Try a different search term."
              : "Plans you generate from AI Plans will be saved here automatically. Plans created before this update aren't in the library."
            }
          </p>
          {!q && (
            <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
              <button
                onClick={importLegacy}
                disabled={importing}
                className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="plans-library-empty-import"
              >
                <Download size={12} /> {importing ? "Importing…" : "Import from older lessons"}
              </button>
              <Link
                to="/coach/plans"
                className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
                data-testid="plans-library-empty-cta"
              >
                <Sparkles size={12} /> Draft your first plan
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const Icon = TYPE_ICON[p.lesson_type] || UserIcon;
            return (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => open(p)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(p); } }}
                className="card-surface p-5 text-left hover:border-[#38BDF8]/40 transition-colors group cursor-pointer"
                data-testid={`plan-card-${p.id}`}
              >
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#A3A3A3] mb-2">
                  <Icon size={12} />
                  <span>{(p.lesson_type || "plan").replace("_", " ")}</span>
                  {p.duration_mins ? <span>· {p.duration_mins} min</span> : null}
                  {p.courts ? <span>· {p.courts} ct</span> : null}
                </div>
                <div className="font-display text-lg font-black uppercase tracking-tight line-clamp-2">{p.title}</div>
                {p.student_name && (
                  <div className="text-[11px] text-[#38BDF8] mt-1 font-bold">
                    {p.student_name}{p.skill_level ? ` · DUPR ${p.skill_level}` : ""}
                  </div>
                )}
                {p.student_names && p.student_names.length > 1 && (
                  <div className="text-[11px] text-[#38BDF8] mt-1 font-bold truncate">
                    {p.student_names.slice(0, 3).join(", ")}{p.student_names.length > 3 ? ` +${p.student_names.length - 3}` : ""}
                  </div>
                )}
                {p.focus && (
                  <div className="text-xs text-white/80 mt-2 line-clamp-2 whitespace-pre-line">{p.focus}</div>
                )}
                <div className="text-[10px] text-[#A3A3A3] mt-3 uppercase tracking-wider">{formatDate(p.created_at)}</div>
                <div className="flex items-center gap-2 mt-4 opacity-90">
                  <button
                    onClick={(e) => share(p, e)}
                    disabled={sharingId === p.id}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 border border-white/15 hover:border-[#38BDF8] hover:text-[#38BDF8] inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`plan-share-${p.id}`}
                  >
                    <Share2 size={10} />
                    {p.is_public ? "Copy link" : "Share"}
                  </button>
                  <button
                    onClick={(e) => remove(p, e)}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 border border-white/15 hover:border-rose-400 hover:text-rose-400 inline-flex items-center gap-1"
                    data-testid={`plan-delete-${p.id}`}
                  >
                    <Trash2 size={10} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!openPlan} onOpenChange={(v) => !v && setOpenPlan(null)}>
        <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="plan-detail-dialog">
          {openPlan && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight text-2xl">{openPlan.title}</DialogTitle>
              </DialogHeader>
              <div className="text-[11px] text-[#A3A3A3] uppercase tracking-wider flex flex-wrap gap-3 mb-4">
                <span>{(openPlan.lesson_type || "plan").replace("_", " ")}</span>
                {openPlan.duration_mins ? <span>{openPlan.duration_mins} min</span> : null}
                {openPlan.courts ? <span>{openPlan.courts} courts</span> : null}
                {openPlan.group_size ? <span>{openPlan.group_size} students</span> : null}
                {openPlan.skill_level ? <span>DUPR {openPlan.skill_level}</span> : null}
                <span>· {formatDate(openPlan.created_at)}</span>
              </div>
              {openPlan.focus && (
                <div className="border-l-2 border-[#38BDF8] pl-3 mb-5 text-sm text-white/85 whitespace-pre-line">
                  {openPlan.focus}
                </div>
              )}
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                <button
                  onClick={() => { navigator.clipboard.writeText(openPlan.plan_text || ""); toast.success("Copied"); }}
                  className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2"
                >
                  <Copy size={12} /> Copy plan text
                </button>
                <button
                  onClick={() => share({ id: openPlan.id })}
                  className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2"
                  data-testid="plan-detail-share"
                >
                  <Share2 size={12} /> Get share link
                </button>
              </div>
              <div className="prose prose-invert max-w-none text-sm">
                <Markdown>{openPlan.plan_text || ""}</Markdown>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
